import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  api,
  loginHref,
  NotSignedIn,
  subscribeLive,
  type ProjectInfo,
  type QueueItem,
  type Status,
  type Settlement,
  type Structure,
  type User,
} from "./lib/api";
import { Queue } from "./components/Queue";
import { Inspector } from "./components/Inspector";
import { Viewer, type ViewerHandle } from "./components/Viewer";
import { AdminBar } from "./components/AdminBar";
import { SettlementTab } from "./components/SettlementTab";

// The curation screen: queue on the left, the build in the middle, its
// placement rule on the right. The flow is the same one the desktop tool has --
// claim the next structure nobody is working on, judge it, sign it off, get the
// next one -- except that here the queue is shared, so two curators never land
// on the same build.

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [project, setProject] = useState<ProjectInfo | null>(null);
  const [items, setItems] = useState<QueueItem[]>([]);
  const [current, setCurrent] = useState<Structure | null>(null);
  const [filter, setFilter] = useState<Status | "">("pending");
  const [placement, setPlacement] = useState("");
  const [use, setUse] = useState("");
  const [sort, setSort] = useState("");
  const [search, setSearch] = useState("");
  // Two tabs: the builds one by one, and the settlements they form. Editing a
  // settlement inline on eight separate builds is what this replaces.
  const [tab, setTab] = useState<"structures" | "settlements">("structures");
  // Kept here as well as in the settlements tab: the inspector says what a
  // build's settlement is missing, so a verdict is never given blind.
  const [settlements, setSettlements] = useState<Settlement[]>([]);

  // The last few verdicts, so a misclick can be taken back.
  const [undo, setUndo] = useState<{ id: number; from: Status; name: string }[]>([]);
  const [busy, setBusy] = useState(false);
  // Who else is here, and whether the build on screen belongs to someone else.
  const [online, setOnline] = useState<{ sub: string; name: string }[]>([]);
  const [readOnly, setReadOnly] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const viewer = useRef<ViewerHandle>(null);

  const say = useCallback((msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast((t) => (t === msg ? null : t)), 6000);
  }, []);

  // --- session ---------------------------------------------------------------
  useEffect(() => {
    api
      .me()
      .then((m) => setUser(m.user))
      .catch((e) => {
        if (e instanceof NotSignedIn) setUser(null);
        else setAuthError(String(e.message ?? e));
      });
  }, []);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const [p, q, st] = await Promise.all([
        api.project(),
        api.queue({ status: filter, q: search, placement, use, sort }),
        api.settlements(),
      ]);
      setProject(p);
      setItems(q.items);
      setSettlements(st.settlements);
    } catch (e) {
      if (e instanceof NotSignedIn) setUser(null);
      else say(String((e as Error).message));
    }
  }, [user, filter, search, placement, use, sort, say]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Live updates: the queue, the progress counter and who is online follow
  // what everyone else does, instead of going stale until the next refetch.
  useEffect(() => {
    if (!user) return;
    return subscribeLive((e) => {
      if (e.type === "presence") {
        setOnline(e.users);
        return;
      }
      if (e.type === "import") {
        say(`${e.by} hat ${e.count} Strukturen importiert`);
        void refresh();
        return;
      }
      // Someone took, judged or edited a build: the list and the counters
      // change, and if it is the one on screen we want to know.
      void refresh();
      if (e.type === "verdict" && current && e.id === current.id)
        say(`${e.by} hat diese Struktur als "${e.status}" eingestuft`);
    });
  }, [user, refresh, say, current]);

  // --- picking a structure ---------------------------------------------------
  const open = useCallback(
    async (id: number) => {
      setBusy(true);
      try {
        // Take the structure before showing it. If someone else holds it the
        // view stays read-only rather than letting two people edit and the
        // last save win.
        const claim = await api.claimOne(id);
        setReadOnly(claim.ok ? null : (claim.heldBy ?? "jemand anderem"));
        const s = await api.structure(id);
        setCurrent(s);
        await viewer.current?.show(s);
      } catch (e) {
        say(String((e as Error).message));
      } finally {
        setBusy(false);
      }
    },
    [say],
  );

  const claimNext = useCallback(async () => {
    setBusy(true);
    try {
      const { id } = await api.claim();
      if (id === null) {
        say("Alle Bauten sind beurteilt — die Warteschlange ist leer.");
        setCurrent(null);
        viewer.current?.clear();
      } else {
        await open(id);
      }
      await refresh();
    } catch (e) {
      say(String((e as Error).message));
    } finally {
      setBusy(false);
    }
  }, [open, refresh, say]);

  const decide = useCallback(
    async (status: Status) => {
      if (!current || busy) return;
      setBusy(true);
      try {
        const previous = current.status;
        await api.verdict(current.id, status);
        setUndo((u) =>
          [{ id: current.id, from: previous, name: current.name }, ...u].slice(0, 20),
        );
        await refresh();
        const { id } = await api.claim();
        if (id === null) {
          setCurrent(null);
          viewer.current?.clear();
          say("Fertig — jeder Bau ist beurteilt.");
        } else {
          await open(id);
        }
      } catch (e) {
        say(String((e as Error).message));
      } finally {
        setBusy(false);
      }
    },
    [current, busy, refresh, open, say],
  );

  const undoLast = useCallback(async () => {
    const last = undo[0];
    if (!last) {
      say("Nichts zum Zurücknehmen.");
      return;
    }
    try {
      await api.verdict(last.id, last.from);
      setUndo((u) => u.slice(1));
      say(`„${last.name}" wieder auf „${last.from}" gesetzt`);
      await refresh();
    } catch (e) {
      say(String((e as Error).message));
    }
  }, [undo, refresh, say]);

  // Keyboard: the queue is meant to be driven without the mouse.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      // Never steal keys from a field the curator is typing in.
      if (el && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) return;
      if (!e.ctrlKey) return;
      if (e.key === "Enter") {
        e.preventDefault();
        void decide("approved");
      } else if (e.key.toLowerCase() === "l") {
        e.preventDefault();
        void decide("later");
      } else if (e.key === "Backspace") {
        e.preventDefault();
        void decide("rejected");
      } else if (e.key.toLowerCase() === "n") {
        e.preventDefault();
        void claimNext();
      } else if (e.key.toLowerCase() === "z") {
        e.preventDefault();
        void undoLast();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [decide, claimNext, undoLast]);

  const progress = project?.progress;
  const done = progress ? progress.total - progress.pending : 0;

  const grouped = useMemo(() => {
    const by = new Map<string, QueueItem[]>();
    for (const it of items) {
      const k = it.folder || "(kein Ordner)";
      if (!by.has(k)) by.set(k, []);
      by.get(k)!.push(it);
    }
    return [...by.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [items]);

  // --- gates -----------------------------------------------------------------
  if (authError)
    return (
      <div className="gate">
        <h1>Structure Studio</h1>
        <p className="error">{authError}</p>
      </div>
    );

  if (!user)
    return (
      <div className="gate">
        <h1>Structure Studio</h1>
        <p>Die Bauten des Weltgenerators gemeinsam durchsehen.</p>
        <a className="primary" href={loginHref()}>
          Anmelden
        </a>
        <p className="hint">
          Du wirst zur Anmeldung deiner Organisation geschickt und danach
          hierher zurückgebracht.
        </p>
      </div>
    );

  return (
    <div className="app">
      <header>
        <strong>{project?.name || "Structure Studio"}</strong>
        {progress && (
          <span className="progress">
            {done} / {progress.total} beurteilt
            <em> ✓{progress.approved} ✕{progress.rejected} …{progress.later}</em>
          </span>
        )}
        <span className="spacer" />
        {undo.length > 0 && (
          <button className="linkish undo" onClick={() => void undoLast()}>
            ↶ Rückgängig ({undo.length}) <kbd>Ctrl</kbd>+<kbd>Z</kbd>
          </button>
        )}
        {user.admin && (
          <AdminBar onChanged={refresh} say={say} progress={progress} />
        )}
        {online.length > 1 && (
          <span
            className="who"
            title={online.map((u) => u.name).join(", ")}
          >
            {online.length} online
          </span>
        )}
        <span className="who" title={user.email}>
          {user.name}
          {user.admin ? " (admin)" : ""}
        </span>
        <a className="linkish" href="/auth/logout">
          Sign out
        </a>
      </header>

      <nav className="tabs">
        <button
          className={tab === "structures" ? "on" : ""}
          onClick={() => setTab("structures")}
        >
          Strukturen
        </button>
        <button
          className={tab === "settlements" ? "on" : ""}
          onClick={() => setTab("settlements")}
        >
          Siedlungen
        </button>
      </nav>

      {tab === "settlements" ? (
        <SettlementTab
          items={items}
          say={say}
          onChanged={refresh}
          onPick={(id) => {
            setTab("structures");
            void open(id);
          }}
        />
      ) : (
      <div className="body">
        <Queue
          groups={grouped}
          currentId={current?.id ?? null}
          filter={filter}
          placement={placement}
          use={use}
          sort={sort}
          search={search}
          me={user.sub}
          onFilter={setFilter}
          onPlacement={setPlacement}
          onUse={setUse}
          onSort={setSort}
          onSearch={setSearch}
          onPick={open}
          onClaim={claimNext}
          busy={busy}
        />

        <main>
          <Viewer ref={viewer} onError={say} />
        </main>

        <Inspector
          structure={current}
          busy={busy}
          readOnly={readOnly}
          settlements={settlements}
          onClaim={claimNext}
          onPatch={async (fields) => {
            if (!current) return;
            try {
              const updated = await api.patch(current.id, fields);
              setCurrent(updated);
              // The view has to follow immediately -- the y offset moves the
              // ground plane, and judging the seating is the whole point.
              viewer.current?.update(updated);
              void refresh();
            } catch (e) {
              say(String((e as Error).message));
            }
          }}
          onDecide={decide}
          onBatch={async (scope, withStatus) => {
            if (!current) return;
            try {
              const r = await api.batch(current.id, scope, {
                fields: true,
                status: withStatus ? current.status : undefined,
                onlyPending: !withStatus,
              });
              say(`auf ${r.changed} weitere Bauten übertragen`);
              await refresh();
            } catch (e) {
              say(String((e as Error).message));
            }
          }}
          onGeometry={async (op) => {
            if (!current) return;
            setBusy(true);
            try {
              const bytes = await viewer.current!.applyGeometry(op);
              if (bytes) {
                await api.putSchem(current.id, bytes);
                const fresh = await api.structure(current.id);
                setCurrent(fresh);
                await viewer.current!.show(fresh);
                void refresh();
              }
            } catch (e) {
              say(String((e as Error).message));
            } finally {
              setBusy(false);
            }
          }}
        />
      </div>
      )}

      {toast && (
        <div className="toast" onClick={() => setToast(null)}>
          {toast}
        </div>
      )}
    </div>
  );
}
